// Estado de los contratos con Santi en el navegador: la lista, cada conversación que se ha abierto (con el borrador de su
// agente, entrega 2), el chat con Santi y qué tiene abierto la ventana «Santi». Se hidrata al pedirlo y vive del
// socket `oficina:pelotas` (alCambioPelotas): sin sondeos. `simular` deja datos fijos para las maquetas y las pruebas
// (ningún fetch de lectura).
// 02-11 (Pau: «quiero que Santi pueda leer y hacer lo que quiera en la oficina, es mi socio»): todas las sesiones leen
// los contratos. Lo que sale hacia Santi firmado como Pau solo lo hace la sesión personal de Pau (el servidor contesta
// 403 SOLO_PAU a cualquier otra); la web, a quien no es Pau, no le enseña esos botones (`useHablaComoPau`).
// 03-11 (ventana «Santi» nueva, docs/pelotas/ux-2026-11-03/propuesta/propuesta.md §5.6): «Hecho» y «Cancelar» son un
// clic con «Deshacer» de 8 s EN LA WEB (construccion.md, «Decisiones técnicas»): el contrato sale de la lista al
// momento (`quitados`), y lo que va al centro sale cuando se va la línea (o al confirmarlo otra cosa: otro «Hecho», cerrar
// la ventana). «Deshacer» lo cancela antes de salir. Si se cierra la pestaña antes, no sale nada: lo que no se envió no
// está hecho. Quien lo ejecuta es ESTE almacén, no la ventana: sigue vivo con la ventana cerrada, y si el centro dice
// que no (o no contesta), el aviso se queda (`fallos`) en la pared y arriba de la ventana hasta que Pau lo vuelve a
// probar (misma clave si no se supo qué pasó; otra si el centro dijo que no) o el contrato ya no está abierto.
// Lo visto: con un contrato (o el chat) en pantalla y Pau mirando (usePauMira), su sesión marca hasta dónde lo ha visto;
// si falla, no se reintenta en bucle: espera creciente y solo con el siguiente cambio o al volver a mirar.
import { create } from 'zustand';
import { ApiHttpError } from '../../../lib/api';
import { envioTermino } from '../errores';
import { useOficinaStore } from '../store';
import { pelotasApi } from './api';
import type { BorradorPedidoDTO, ContratoDTO, HiloAbierto, HiloDTO, HiloGeneralDTO, ListaContratosDTO, MotivoCierre } from './tipos';

/** Lo que manda el servidor por el socket (backend/src/oficina/pelotas/index.ts `CambioPelotas`). */
export interface CambioPelotas {
  agentId?: string | null;
  temaId?: number | null;
  general?: boolean;
}

/** Cuánto dura la línea de «Hecho» / «Cancelar» antes de irse (y sale lo pendiente). */
export const DURACION_LINEA_MS = 8_000;
/** Marcar visto que falló: se espera esto (y el doble cada vez, hasta 10 min) antes de volver a probar. */
export const ESPERA_VISTO_MS = 30_000;
const ESPERA_VISTO_MAX_MS = 10 * 60_000;

/**
 * «Hecho» o «Cancelar» pulsado y aún sin enviar: la línea flotante con «Deshacer». `porque`: Pau pulsó «Decirle por
 * qué» (el tiempo deja de contar y sale con «Enviar»).
 */
export interface ActoPendiente {
  n: number;
  temaId: number;
  titulo: string;
  motivo: MotivoCierre;
  porque: boolean;
  nota: string;
  /** La clave con que saldrá (la de un intento anterior incierto sobre lo mismo, si lo hubo). */
  clave: string;
}

/**
 * Un «Hecho» / «Cancelar» que no llegó: el contrato vuelve a su sitio y el aviso se queda (pared y ventana) hasta que se
 * vuelve a probar bien, Pau lo quita o el contrato deja de estar abierto. `incierto`: no se supo si llegó (red, 5xx) —
 * «Volver a probar» repite la MISMA clave y el centro no lo hace dos veces; si el centro dijo que no (4xx), otra.
 * `explicado`: el centro de antes (mientras la oficina nueva convive con él, hasta que Santi confirme la subida del
 * centro con la regla 12) contestó algo que no se arregla volviendo a probar: `NO_ES_TUYO` (403: lo pidió Santi y aún
 * cierra solo quien lo pidió) o `TABLERO_SIN_CANCELAR` (409: aún no sabe cancelar). Es una respuesta, no un fallo: el
 * aviso lo explica, sin «Volver a probar» ni alarma, y la pared no lo cuenta como «Sin enviar».
 */
export interface FalloActo {
  n: number;
  temaId: number;
  titulo: string;
  motivo: MotivoCierre;
  nota: string;
  clave: string;
  incierto: boolean;
  explicado: RespuestaExplicada | null;
  reintentando: boolean;
}

/** Lo que el centro de antes contesta y no se arregla volviendo a probar (cada uno con su texto en errores.pelotas). */
export const RESPUESTAS_EXPLICADAS = ['NO_ES_TUYO', 'TABLERO_SIN_CANCELAR'] as const;
export type RespuestaExplicada = (typeof RESPUESTAS_EXPLICADAS)[number];

export function respuestaExplicada(e: unknown): RespuestaExplicada | null {
  if (!(e instanceof ApiHttpError)) return null;
  return (RESPUESTAS_EXPLICADAS as readonly string[]).includes(e.code) ? (e.code as RespuestaExplicada) : null;
}

interface EstadoPelotas {
  lista: ListaContratosDTO | null;
  /** No se pudo leer la lista (se reintenta con el siguiente cambio). */
  errorLista: boolean;
  hilos: Record<number, HiloDTO>;
  general: HiloGeneralDTO | null;
  /** El «Pedir algo» preparado de cada agente cuyo chat se ha abierto (null = no tiene). */
  pedidos: Record<string, BorradorPedidoDTO | null>;
  /** El agente cuyo pedido abrió «Revisar» (lo trae «Pedir algo»). */
  pedidoAbierto: string | null;
  /**
   * Qué enseña la ventana «Santi»; null = cerrada. Es un ESPEJO de la URL (?santi=…), que es la verdad: lo escribe
   * OficinaPage. La lista y la ventana no lo tocan: PIDEN abrir o cerrar (`peticion`) y la página mueve la URL.
   */
  abierto: HiloAbierto | null;
  /** La última petición de la lista o de la ventana (abrir un hilo, o cerrar = null); `n` sube con cada una. */
  peticion: { n: number; hilo: HiloAbierto | null } | null;
  simulada: boolean;
  /** «Hecho» / «Cancelar» a la espera de los 8 s (uno a la vez). */
  acto: ActoPendiente | null;
  /** Lo que no llegó al centro (lo más reciente al final). */
  fallos: FalloActo[];
  /** Contratos que Pau acaba de quitar de la lista con «Hecho» / «Cancelar» (enviados o a punto de salir). */
  quitados: Record<number, true>;
  /** Hasta qué seq ha marcado ya esta sesión como visto cada hilo (para no repetirlo). */
  vistos: Record<string, number>;
  /** Marcar visto que falló, por hilo: cuántas veces seguidas y desde cuándo se puede volver a probar. */
  esperaVisto: Record<string, { intentos: number; desde: number }>;
  abrirSanti: (hilo: HiloAbierto) => void;
  cerrarSanti: () => void;
  /** Solo la página: la URL dice qué hay abierto. */
  reflejarAbierto: (hilo: HiloAbierto | null) => void;
  cargarLista: () => Promise<void>;
  cargarHilo: (temaId: number) => Promise<void>;
  cargarGeneral: () => Promise<void>;
  /** «Que lo prepare tu agente» / «Otra versión»: el agente redacta para ese hilo (el borrador llega por el socket). */
  preparar: (hilo: number | 'general') => Promise<void>;
  /** «Descartar» el borrador del agente: fuera ya de la caja y de la fila; si el servidor no lo quita, vuelve con la lista. */
  descartarBorrador: (hilo: number | 'general') => void;
  cargarPedido: (agentId: string) => Promise<void>;
  /** «Revisar»: «Pedir algo» con el pedido que preparó ese agente. */
  abrirPedido: (agentId: string) => void;
  /** «Descartar» el pedido preparado de ese agente (o ya enviado: se olvida). */
  descartarPedido: (agentId: string, enServidor?: boolean) => void;
  /** «Hecho» / «Cancelar»: fuera de la lista ya; al centro, cuando se vaya la línea. Confirma el anterior si lo había. */
  marcar: (c: Pick<ContratoDTO, 'temaId' | 'titulo'>, motivo: MotivoCierre, nota?: string) => void;
  /** «Deshacer»: todo como estaba, sin que a Santi le haya llegado nada. Devuelve el contrato que vuelve. */
  deshacerActo: () => number | null;
  /** «Decirle por qué»: la línea pasa a un campo y deja de contar el tiempo. */
  decirPorque: () => void;
  escribirPorque: (nota: string) => void;
  /** Sale ya al centro lo pendiente («Enviar» del motivo, cerrar la ventana, otro «Hecho»). */
  confirmarActo: () => void;
  /** «Volver a probar» un acto que no llegó. */
  reintentarFallo: (n: number) => Promise<void>;
  /** Pau quita el aviso de un acto que no llegó (el contrato sigue abierto, como estaba). */
  quitarFallo: (n: number) => void;
  /** Esta sesión (la de Pau) ha visto el hilo hasta `hasta`: se apaga su punto blanco. */
  marcarVisto: (hilo: number | 'general', hasta: number) => void;
  simular: (datos: { lista?: ListaContratosDTO | null; hilos?: Record<number, HiloDTO>; general?: HiloGeneralDTO | null; pedidos?: Record<string, BorradorPedidoDTO | null> }) => void;
}

// El reloj de la línea flotante (uno: hay un acto pendiente a la vez).
let relojActo: ReturnType<typeof setTimeout> | null = null;
let contadorActos = 0;

function pararReloj(): void {
  if (relojActo) clearTimeout(relojActo);
  relojActo = null;
}

export const usePelotasStore = create<EstadoPelotas>((set, get) => {
  /**
   * Manda al centro un acto (ya fuera de la línea; la ventana puede estar cerrada). Bien: la lista se pone al día. Mal:
   * el contrato vuelve a su sitio y el aviso se queda en `fallos` (con su clave, para «Volver a probar»).
   */
  async function enviarActo(a: Pick<FalloActo, 'n' | 'temaId' | 'titulo' | 'motivo' | 'nota' | 'clave'>): Promise<void> {
    try {
      await pelotasApi.cerrar(a.temaId, { nota: a.nota.trim() || undefined, motivo: a.motivo }, a.clave);
      set((s) => ({ fallos: s.fallos.filter((f) => f.n !== a.n) }));
      void get().cargarLista();
      if (get().hilos[a.temaId]) void get().cargarHilo(a.temaId);
    } catch (e) {
      const fallo: FalloActo = { ...a, incierto: !envioTermino(e), explicado: respuestaExplicada(e), reintentando: false };
      set((s) => {
        const quitados = { ...s.quitados };
        delete quitados[a.temaId];
        return { quitados, fallos: [...s.fallos.filter((f) => f.n !== a.n && f.temaId !== a.temaId), fallo] };
      });
    }
  }

  /** La línea se va y lo pendiente sale. */
  function soltar(): void {
    pararReloj();
    const a = get().acto;
    if (!a) return;
    set({ acto: null });
    void enviarActo(a);
  }

  return {
    lista: null,
    errorLista: false,
    hilos: {},
    general: null,
    pedidos: {},
    pedidoAbierto: null,
    abierto: null,
    peticion: null,
    simulada: false,
    acto: null,
    fallos: [],
    quitados: {},
    vistos: {},
    esperaVisto: {},
    abrirSanti: (hilo) => set((s) => ({ peticion: { n: (s.peticion?.n ?? 0) + 1, hilo } })),
    cerrarSanti: () => set((s) => ({ peticion: { n: (s.peticion?.n ?? 0) + 1, hilo: null } })),
    reflejarAbierto: (hilo) => set((s) => (s.abierto === hilo ? {} : { abierto: hilo })),
    cargarLista: async () => {
      if (get().simulada) return;
      try {
        const lista = await pelotasApi.lista();
        if (!get().simulada) set((s) => ({ lista, errorLista: false, fallos: vigentes(s.fallos, lista) }));
      } catch {
        if (!get().simulada) set({ errorLista: true });
      }
    },
    cargarHilo: async (temaId) => {
      if (get().simulada) return;
      try {
        const hilo = await pelotasApi.hilo(temaId);
        if (!get().simulada) set((s) => ({ hilos: { ...s.hilos, [temaId]: hilo } }));
      } catch {
        // Se queda lo que había; el siguiente cambio del socket lo vuelve a pedir.
      }
    },
    cargarGeneral: async () => {
      if (get().simulada) return;
      try {
        const general = await pelotasApi.general();
        if (!get().simulada) set({ general });
      } catch {
        // Igual: lo último que se leyó, hasta el siguiente cambio.
      }
    },
    preparar: async (hilo) => {
      await pelotasApi.preparar(hilo);
    },
    descartarBorrador: (hilo) => {
      set((s) => sinBorrador(s, hilo));
      pelotasApi.descartarBorrador(hilo).then(
        () => void get().cargarLista(),
        () => {
          // No se quitó en el servidor: lo que diga él (el borrador vuelve a la fila y a la caja).
          void get().cargarLista();
          if (typeof hilo === 'number' && get().hilos[hilo]) void get().cargarHilo(hilo);
          if (hilo === 'general' && get().general) void get().cargarGeneral();
        },
      );
    },
    cargarPedido: async (agentId) => {
      if (get().simulada) return;
      try {
        const r = await pelotasApi.agenteSanti(agentId);
        if (!get().simulada) set((s) => ({ pedidos: { ...s.pedidos, [agentId]: r.pedido } }));
      } catch {
        // Sin línea «Revisar» hasta el siguiente cambio.
      }
    },
    abrirPedido: (agentId) => {
      set({ pedidoAbierto: agentId });
      get().abrirSanti('pedir');
    },
    descartarPedido: (agentId, enServidor = true) => {
      set((s) => ({ pedidos: { ...s.pedidos, [agentId]: null }, pedidoAbierto: s.pedidoAbierto === agentId ? null : s.pedidoAbierto }));
      if (!enServidor) return;
      pelotasApi.descartarPedido(agentId).catch(() => void get().cargarPedido(agentId));
    },
    marcar: (c, motivo, nota = '') => {
      soltar();
      contadorActos += 1;
      // Un intento anterior sobre lo mismo que no se supo si llegó: este repite su clave (el centro no lo hace dos veces).
      const antes = get().fallos.find((f) => f.temaId === c.temaId);
      const clave = antes && antes.incierto && antes.motivo === motivo ? antes.clave : crypto.randomUUID();
      const acto: ActoPendiente = { n: contadorActos, temaId: c.temaId, titulo: c.titulo, motivo, porque: false, nota, clave };
      set((s) => ({ acto, fallos: s.fallos.filter((f) => f.temaId !== c.temaId), quitados: { ...s.quitados, [c.temaId]: true } }));
      relojActo = setTimeout(() => {
        relojActo = null;
        if (get().acto?.n === acto.n) soltar();
      }, DURACION_LINEA_MS);
    },
    deshacerActo: () => {
      pararReloj();
      const a = get().acto;
      if (!a) return null;
      set((s) => {
        const quitados = { ...s.quitados };
        delete quitados[a.temaId];
        return { acto: null, quitados };
      });
      return a.temaId;
    },
    decirPorque: () => {
      pararReloj();
      set((s) => (s.acto ? { acto: { ...s.acto, porque: true } } : {}));
    },
    escribirPorque: (nota) => set((s) => (s.acto ? { acto: { ...s.acto, nota } } : {})),
    confirmarActo: () => soltar(),
    reintentarFallo: async (n) => {
      const f = get().fallos.find((x) => x.n === n);
      if (!f || f.reintentando || f.explicado) return;
      const clave = f.incierto ? f.clave : crypto.randomUUID();
      set((s) => ({ fallos: s.fallos.map((x) => (x.n === n ? { ...x, clave, reintentando: true } : x)), quitados: { ...s.quitados, [f.temaId]: true } }));
      await enviarActo({ ...f, clave });
    },
    quitarFallo: (n) => set((s) => ({ fallos: s.fallos.filter((f) => f.n !== n) })),
    marcarVisto: (hilo, hasta) => {
      const clave = String(hilo);
      if ((get().vistos[clave] ?? 0) >= hasta) return;
      // Falló hace poco: nada hasta que pase la espera (y entonces, solo con el siguiente cambio o al volver a mirar).
      const espera = get().esperaVisto[clave];
      if (espera && Date.now() < espera.desde) return;
      set((s) => ({ vistos: { ...s.vistos, [clave]: hasta }, lista: sinNuevos(s.lista, hilo) }));
      pelotasApi.visto(hilo, hasta).then(
        () => {
          set((s) => {
            const esperaVisto = { ...s.esperaVisto };
            delete esperaVisto[clave];
            return { esperaVisto };
          });
          void get().cargarLista();
        },
        () => {
          // No quedó marcado: se olvida (el punto vuelve con la lista del servidor) y se espera, cada vez el doble.
          set((s) => {
            const vistos = { ...s.vistos };
            if (vistos[clave] === hasta) delete vistos[clave];
            const intentos = (s.esperaVisto[clave]?.intentos ?? 0) + 1;
            const ms = Math.min(ESPERA_VISTO_MS * 2 ** (intentos - 1), ESPERA_VISTO_MAX_MS);
            return { vistos, esperaVisto: { ...s.esperaVisto, [clave]: { intentos, desde: Date.now() + ms } } };
          });
          void get().cargarLista();
        },
      );
    },
    simular: (d) =>
      set((s) => ({
        simulada: true,
        lista: d.lista === undefined ? s.lista : d.lista,
        fallos: d.lista ? vigentes(s.fallos, d.lista) : s.fallos,
        hilos: d.hilos ?? s.hilos,
        general: d.general === undefined ? s.general : d.general,
        pedidos: d.pedidos ?? s.pedidos,
      })),
  };
});

/** El estado sin el borrador de ese hilo (en la caja y en la fila), antes de que conteste el servidor. */
function sinBorrador(s: EstadoPelotas, hilo: number | 'general'): Partial<EstadoPelotas> {
  if (hilo === 'general') {
    return {
      general: s.general ? { ...s.general, borrador: null } : s.general,
      lista: s.lista ? { ...s.lista, general: { ...s.lista.general, borrador: false } } : s.lista,
    };
  }
  const h = s.hilos[hilo];
  return {
    hilos: h ? { ...s.hilos, [hilo]: { ...h, borrador: null } } : s.hilos,
    lista: s.lista ? { ...s.lista, contratos: s.lista.contratos.map((c) => (c.temaId === hilo ? { ...c, borrador: false } : c)) } : s.lista,
  };
}

/** Los avisos que aún valen: el contrato sigue abierto (si ya no lo está, lo cerró alguien, o sí llegó). */
function vigentes(fallos: FalloActo[], lista: ListaContratosDTO): FalloActo[] {
  const abiertos = new Set(lista.contratos.map((c) => c.temaId));
  const quedan = fallos.filter((f) => f.reintentando || abiertos.has(f.temaId));
  return quedan.length === fallos.length ? fallos : quedan;
}

/** La lista con el punto blanco de ese hilo apagado (al marcarlo visto, sin esperar a que vuelva la lista). */
function sinNuevos(lista: ListaContratosDTO | null, hilo: number | 'general'): ListaContratosDTO | null {
  if (!lista) return lista;
  if (hilo === 'general') return lista.general.nuevos ? { ...lista, general: { ...lista.general, nuevos: 0 } } : lista;
  if (!lista.contratos.some((c) => c.temaId === hilo && c.nuevos)) return lista;
  return { ...lista, contratos: lista.contratos.map((c) => (c.temaId === hilo ? { ...c, nuevos: 0 } : c)) };
}

/** El socket avisa de que algo cambió: se vuelve a pedir SOLO lo que está en pantalla y le afecta. */
export function alCambioPelotas(p: CambioPelotas): void {
  const s = usePelotasStore.getState();
  if (s.simulada) return;
  void s.cargarLista();
  if (p.general || (p.temaId === undefined && p.agentId === undefined)) {
    if (s.general) void s.cargarGeneral();
  }
  if (typeof p.temaId === 'number' && s.hilos[p.temaId]) void s.cargarHilo(p.temaId);
  // El «Pedir algo» preparado de un agente cuyo chat está a la vista.
  if (p.agentId && p.agentId in s.pedidos) void s.cargarPedido(p.agentId);
  // Un cambio sin señas (el centro cayó o volvió): todo lo que esté abierto, por si acaso.
  if (p.temaId === undefined && p.agentId === undefined && !p.general) {
    for (const id of Object.keys(s.hilos)) void s.cargarHilo(Number(id));
    for (const id of Object.keys(s.pedidos)) void s.cargarPedido(id);
  }
}

/** Lo que enseña la ventana «Santi»: el hilo abierto, cargado si hace falta («Pedir algo» no carga nada). */
export function pedirHilo(hilo: HiloAbierto): void {
  const s = usePelotasStore.getState();
  if (hilo === 'general') void s.cargarGeneral();
  else if (hilo !== 'pedir') void s.cargarHilo(hilo);
}

// ── El último hilo que Pau tenía abierto (en el navegador): la ventana vuelve ahí si no hay nada nuevo ──
const CLAVE_ULTIMO = 'oficina:santi:ultimo';

export function recordarUltimo(hilo: HiloAbierto): void {
  if (hilo === 'pedir') return;
  try {
    localStorage.setItem(CLAVE_ULTIMO, String(hilo));
  } catch {
    // Sin almacenamiento (modo privado): la ventana abrirá en el primero de la lista; nada que avisar.
  }
}

function ultimoRecordado(): HiloAbierto | null {
  try {
    const v = localStorage.getItem(CLAVE_ULTIMO);
    if (v === 'general') return 'general';
    const n = Number(v);
    return v && Number.isInteger(n) ? n : null;
  } catch {
    return null;
  }
}

/**
 * Dónde se abre la ventana (propuesta §5.1): en lo más reciente de Santi que no has visto; si no hay nada nuevo, en el
 * último que tenías abierto (si sigue abierto); si no, en el primero de la lista; sin contratos, en «Chat con Santi».
 * Nunca «en el más viejo».
 */
export function hiloAlAbrir(lista: ListaContratosDTO | null): HiloAbierto {
  if (!lista) return ultimoRecordado() ?? 'general';
  if (lista.pared.nuevos > 0 && lista.pared.masReciente !== null) return lista.pared.masReciente;
  const ultimo = ultimoRecordado();
  if (ultimo === 'general' || (typeof ultimo === 'number' && lista.contratos.some((c) => c.temaId === ultimo))) return ultimo;
  return lista.contratos[0]?.temaId ?? 'general';
}

/** Para las pruebas y las maquetas: todo a cero (también los relojes de la línea y las claves pendientes). */
export function reiniciarPelotas(): void {
  pararReloj();
  usePelotasStore.setState({ lista: null, errorLista: false, hilos: {}, general: null, pedidos: {}, pedidoAbierto: null, abierto: null, peticion: null, simulada: false, acto: null, fallos: [], quitados: {}, vistos: {}, esperaVisto: {} });
}

/**
 * 02-11 — ¿Quien mira puede hacer lo que sale hacia Santi como Pau (escribirle, pedirle algo, «Hecho», «Cancelar»,
 * pedir o descartar un borrador de su agente, marcar lo visto)? Solo Pau. Un socio lo lee todo y habla con el agente;
 * esos botones no le salen: en su sitio, una línea que lo dice. Mientras no se sabe quién mira (antes de hidratar la oficina), sí: es lo
 * de siempre en la oficina (CuentasClaude) y el servidor es quien manda (403 SOLO_PAU).
 */
export function useHablaComoPau(): boolean {
  return useOficinaStore((s) => s.yo?.rol !== 'socio');
}
